import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogTitleProps,
  IconButton,
  Stack,
  StackProps,
  SxProps,
  Typography,
} from '@mui/material';

import { Close as CloseIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useConfirm } from 'material-ui-confirm';
import { LoadingButton } from '@mui/lab';
import { TP_COLORS } from '../../../../../constants';
import { useAppSelector } from '../../../../../hooks';

import { getCoursesState } from '../../../../../store/V2/CoursesSlice';
import { ITrialClass, TRIAL_STATUS } from '../../../../../store/V2/TrialsSlice';
import {
  TAdminUpdateTrial,
  updateTrialAsAdmin,
} from '../../../../../store/V2/TrialsSlice/adminApi';
import {
  deleteCourseSectionByIdAsAdmin,
  ISection,
  LESSON_TYPES,
  updateCourseSectionAsAdmin,
} from '../../../../../store/V2/SectionSlice';

import SectionItem from '../components/SectionItem';
import BasicLoader from '../../../../../components/Loaders/BasicLoader';

const actionButtonStyles: SxProps = {
  width: '120px',
  height: '40px',
  fontSize: '12px',
  fontWeight: 600,
  borderRadius: '6px',
};

const dialogTitleProps: DialogTitleProps = {
  sx: {
    padding: '0px',
    fontWeight: '600',
    fontSize: '16px',
  },
};

const slotStackProps: StackProps = {
  alignItems: 'center',
  justifyContent: 'center',
  height: '100px',
};

const updateTree = (
  tree: ICustomSection[],
  id: string,
  newData: Partial<ICustomSection>
) => {
  let index = 0;

  if (!Array.isArray(tree)) return false;

  // eslint-disable-next-line no-restricted-syntax
  for (const node of tree) {
    if (node._id === id) {
      tree[index] = {
        ...tree[index],
        ...newData,
      };

      return true;
    }

    if (node.sections && node.sections.length > 0) {
      if (updateTree(node.sections, id, newData)) {
        return true;
      }
    }

    index += 1;
  }

  return false;
};

const deleteFromTree = (tree: ICustomSection[], id: string): boolean => {
  let index = 0;

  if (!Array.isArray(tree)) return false;

  // eslint-disable-next-line no-restricted-syntax
  for (const node of tree) {
    if (node._id === id) {
      tree.splice(index, 1);

      return true;
    }

    if (node.sections && node.sections.length > 0) {
      if (deleteFromTree(node.sections, id)) {
        return true;
      }
    }

    index += 1;
  }

  return false;
};

export interface ICustomSection extends ISection {
  originalSectionId?: string;
  loading?: boolean;
  error?: string;
}

interface SuccessConfirmationModalProps {
  open: boolean;
  trialData: ITrialClass;
  onClose: () => void;
}

const SuccessConfirmationModal: React.FC<SuccessConfirmationModalProps> = ({
  open,
  trialData,
  onClose,
}) => {
  const confirm = useConfirm();
  const dispatch = useDispatch();
  const coursesState = useAppSelector(getCoursesState);

  const { adminCourse, status } = coursesState;

  // State
  const [isLoading, setLoading] = useState(false);
  const [sections, setSections] = useState<Array<ICustomSection>>([]);

  useEffect(() => {
    if (adminCourse && adminCourse.sections.length) {
      const newSections = adminCourse.sections.filter(
        (item) => item.lessonType !== LESSON_TYPES.TRIAL
      );
      setSections(newSections);
    }
  }, [adminCourse]);

  const toggleLoading = () => {
    setLoading((prevState) => !prevState);
  };

  const updateSectionItemAttributes = (
    id: string,
    attributes: Partial<ICustomSection>
  ) => {
    setSections((prevState) => {
      const newSections = JSON.parse(JSON.stringify([...prevState]));
      updateTree(newSections, id, attributes);

      return newSections;
    });
  };

  const deleteSectionItem = (id: string) => {
    confirm({
      title: 'Are you sure?',
      description: 'Do you want to delete this item?',
    }).then(async () => {
      setSections((prevState) => {
        const newSections = JSON.parse(JSON.stringify([...prevState]));
        deleteFromTree(newSections, id);

        return newSections;
      });
      updateSectionItemAttributes(id, {
        loading: true,
      });
      await dispatch(deleteCourseSectionByIdAsAdmin(id));

      updateSectionItemAttributes(id, {
        loading: false,
      });
    });
  };

  const handleSubmit = async () => {
    // eslint-disable-next-line no-restricted-syntax
    for await (const sectionItem of sections) {
      updateSectionItemAttributes(sectionItem._id, {
        loading: true,
      });
      await dispatch(
        updateCourseSectionAsAdmin({
          _id: sectionItem._id,
          lessonType: 'LESSON',
        })
      );

      updateSectionItemAttributes(sectionItem._id, {
        loading: false,
      });

      if (sectionItem.sections && sectionItem.sections.length) {
        const subSections = sectionItem.sections;
        // eslint-disable-next-line no-restricted-syntax
        for await (const subSectionItem of subSections) {
          updateSectionItemAttributes(subSectionItem._id, {
            loading: true,
          });
          await dispatch(
            updateCourseSectionAsAdmin({
              _id: subSectionItem._id,
              lessonType: 'LESSON',
              dateTime: subSectionItem.dateTime,
              duration: subSectionItem.duration,
            })
          );

          updateSectionItemAttributes(subSectionItem._id, {
            loading: false,
          });
        }
      }
    }

    if (trialData._id) {
      toggleLoading();

      const apiData: TAdminUpdateTrial = {
        _id: trialData._id,
        status: TRIAL_STATUS.SUCCEED,
      };
      await dispatch(updateTrialAsAdmin(apiData));

      toggleLoading();
    }

    onClose();
  };

  return (
    <Dialog
      maxWidth="lg"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { padding: '20px', borderRadius: '20px' } }}
    >
      <DialogTitle {...dialogTitleProps}>Mark as a Success Trial</DialogTitle>
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', md: '12px' },
          top: { xs: '7px', md: '12px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <Stack gap="10px">
          {sections.length
            ? sections.map((sectionItem, index) => {
                const _keyName = `${index + 1}`;
                const _orderNumber = `${index + 1}`;

                return (
                  <Stack key={_keyName}>
                    <SectionItem
                      keyName={_keyName}
                      orderNumber={_orderNumber}
                      section={sectionItem}
                      onDelete={deleteSectionItem}
                      updateAttributes={updateSectionItemAttributes}
                    />
                  </Stack>
                );
              })
            : null}
          {status === 'loading' && (
            <Stack {...slotStackProps}>
              <BasicLoader />
            </Stack>
          )}
          {status !== 'loading' && !sections.length ? (
            <Stack {...slotStackProps}>
              <Typography fontSize="14px">No classes are available</Typography>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          disableElevation
          variant="contained"
          color="inherit"
          onClick={onClose}
          sx={{
            ...actionButtonStyles,
            backgroundColor: TP_COLORS.border.color1,
          }}
        >
          Dismiss
        </Button>
        <LoadingButton
          disableElevation
          loading={isLoading}
          color="success"
          variant="contained"
          onClick={handleSubmit}
          sx={actionButtonStyles}
        >
          Success
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default SuccessConfirmationModal;
